import moment, { type Moment } from "moment";
import { CompetitionStageDto, type PortalSourcingEventDetailDto, type ProcedureType } from "../../../../autogen/bff-api";
import { isDesignContest, isDpsScheme, isQualificationScheme } from "../../buyer/wizard/utils/procedure";

// Two-stage events (begrenset anbudskonkurranse) have no tilbudsfrist until the invitation —
// during stage 1 the request deadline is the phase-relevant deadline the portal shows. During
// the qualification window (requests closed, invitation pending) there is no active deadline.
export const effectiveDeadlineIso = (event: PortalSourcingEventDetailDto): string | null => {
  if (event.competitionStage === CompetitionStageDto.Qualification) return null;
  if (event.competitionStage === CompetitionStageDto.InterestOpen) return null;
  return event.bidSubmissionDeadline ?? event.requestDeadline ?? null;
};

// Phase comes from the server-derived competitionStage — never inferred from which deadline
// fields happen to be null (that inference breaks for procedures where both coexist, e.g. DPS).
export const isRequestPhase = (event: Pick<PortalSourcingEventDetailDto, "competitionStage">): boolean =>
  event.competitionStage === CompetitionStageDto.RequestsOpen;

// What the shown deadline is called. An ordning keeps admitting suppliers after the announced
// frist (FOA § 26-6), so its date is the first intake rather than the last chance to take part.
// t('Bid deadline') t('Deadline for entries') t('Deadline for requests to participate') t('Deadline for the first intake')
export const deadlineLabelKey = (
  event: Pick<PortalSourcingEventDetailDto, "competitionStage" | "procedureType">,
): string =>
  !isRequestPhase(event) ? (isDesignContest(event.procedureType) ? "Deadline for entries" : "Bid deadline")
  : isDpsScheme(event.procedureType) ? "Deadline for the first intake"
  : "Deadline for requests to participate";

// FOA § 26-4 (2): a dynamic purchasing system admits suppliers for as long as it runs, past its first intake too.
export const continuousAdmissionUntil = (
  event: { procedureType?: ProcedureType | null; dpsValidTo?: string | null },
  deadlineIso: string | null | undefined,
  now: Moment = moment(),
): string | null =>
  isDpsScheme(event.procedureType) &&
  event.dpsValidTo &&
  moment(event.dpsValidTo).isAfter(now) &&
  deadlineIso &&
  !moment(deadlineIso).isAfter(now)
    ? event.dpsValidTo
    : null;

export const qualificationSchemeExpiry = (
  event: { procedureType?: ProcedureType | null; qualificationSchemeValidTo?: string | null },
  deadlineIso: string | null | undefined,
): string | null =>
  isQualificationScheme(event.procedureType) ? (event.qualificationSchemeValidTo ?? deadlineIso ?? null) : null;
